// Sorting "My hike lists" (/user/hike-lists) — the page listing the user's own lists. The server
// does the ordering (`HikeList.order_for_owner_sort`); this decides which sort is asked for and
// reports a change of it. Nothing is persisted: the page opens on the default every time.
//
// Twins: Android `modules/hikeLists/models/HikeListSort.kt`, iOS
// `Modules/HikeLists/Models/HikeListSort.swift`. Same options, order, default, event name and
// properties on all three.

/** The sorts My hike lists offers, in the order the sort menu lists them — the server's values. */
export const HIKE_LIST_SORT_ORDERS = ['created_desc', 'name_asc', 'hikes_count_asc', 'hikes_count_desc'] as const
export type HikeListSortOrder = typeof HIKE_LIST_SORT_ORDERS[number]

export const DEFAULT_HIKE_LIST_SORT: HikeListSortOrder = 'created_desc'

/** "Hike List Sorted" — sent when the user applies a sort other than the default. */
export const HIKE_LIST_SORTED_EVENT = 'Hike List Sorted'

/** The only screen with a sort today; the property is there so a later one is told apart. */
export const SCREEN_MY_LISTS = 'my_lists'

export function isDefaultSort (order: HikeListSortOrder): boolean {
  return order === DEFAULT_HIKE_LIST_SORT
}

/** An unknown or missing value falls back to the default. */
export function sortFromValue (value: string | null | undefined): HikeListSortOrder {
  return (HIKE_LIST_SORT_ORDERS as readonly string[]).includes(value ?? '')
    ? value as HikeListSortOrder
    : DEFAULT_HIKE_LIST_SORT
}

export function sortedEventProperties (order: HikeListSortOrder, screen: string = SCREEN_MY_LISTS): Record<string, string> {
  return { screen, sort: order }
}

export type HikeListSortAnalytics = (event: string, properties: Record<string, string>) => void

export const posthogSortAnalytics: HikeListSortAnalytics = (event, properties) => {
  window.posthog?.capture(event, properties)
}

/** What applying a sort from the sort menu does: reports it when it is a real change to a non-default sort. */
export function hikeListSortSelection (analytics: HikeListSortAnalytics) {
  return {
    apply (current: string, chosen: string): HikeListSortOrder {
      const order = sortFromValue(chosen)
      if (order !== sortFromValue(current) && !isDefaultSort(order)) {
        analytics(HIKE_LIST_SORTED_EVENT, sortedEventProperties(order))
      }
      return order
    }
  }
}
